// Shared pickers for security/filter condition editors (Schema + Business
// Area designers): insert a runtime variable into a SQL condition, and add a
// functional role to an APPLIES_TO_ROLES list. Both are thin, style-neutral
// selects that reset after each pick so they behave like "insert" menus.

import { useEffect, useState } from 'react'
import { listProjectVariables } from '@/api/projectVariables'
import { listFunctionalRoles } from '@/api/team'

export interface VariableToken {
  token: string
  label: string
}

// Standard tokens bound to the signed-in user/session at run time — kept in
// sync with backend bi_sql/schema_join_builder.SECURITY_TOKEN_ALIASES.
export const STANDARD_SQL_TOKENS: VariableToken[] = [
  { token: ':current_user_id', label: 'Signed-in user id' },
  { token: ':current_user_email', label: 'Signed-in user email' },
  { token: ':current_role', label: "Signed-in user's role" },
  { token: ':current_date', label: 'Current date' },
  { token: ':current_timestamp', label: 'Current timestamp' },
]

/** Standard user/session tokens + the project's own variables (:name). */
export function useSqlVariableTokens(project: string): VariableToken[] {
  const [tokens, setTokens] = useState<VariableToken[]>(STANDARD_SQL_TOKENS)
  useEffect(() => {
    if (!project) return
    let cancelled = false
    listProjectVariables(project)
      .then((vars) => {
        if (cancelled) return
        const projectTokens = (vars || [])
          .filter((v) => v.name)
          .map((v) => ({
            token: `:${v.name}`,
            label: v.description
              ? `${v.description} (project variable)`
              : 'project variable',
          }))
        setTokens([...STANDARD_SQL_TOKENS, ...projectTokens])
      })
      .catch(() => { /* variables are optional — keep the standard tokens */ })
    return () => { cancelled = true }
  }, [project])
  return tokens
}

/** Functional (runtime) roles for role dropdowns; typing new roles stays allowed. */
export function useFunctionalRoles(project: string): string[] {
  const [roles, setRoles] = useState<string[]>([])
  useEffect(() => {
    if (!project) return
    let cancelled = false
    listFunctionalRoles(project)
      .then((res) => { if (!cancelled) setRoles(res?.roles || []) })
      .catch(() => { if (!cancelled) setRoles([]) })
    return () => { cancelled = true }
  }, [project])
  return roles
}

const pickerStyle: React.CSSProperties = {
  padding: '6px 8px', border: '1px solid var(--color-border)', borderRadius: 6,
  fontSize: 13, background: 'var(--color-bg)', color: 'var(--color-text-secondary)',
  outline: 'none', maxWidth: 170,
}

/** "Insert variable…" menu — appends the picked token via onInsert. */
export function VariableInsert({ tokens, onInsert, disabled }: {
  tokens: VariableToken[]
  onInsert: (token: string) => void
  disabled?: boolean
}) {
  return (
    <select
      style={pickerStyle}
      value=""
      disabled={disabled}
      title="Insert a runtime variable into the condition"
      onChange={(e) => { if (e.target.value) onInsert(e.target.value) }}
    >
      <option value="">+ variable…</option>
      {tokens.map((t) => (
        <option key={t.token} value={t.token}>{t.token} — {t.label}</option>
      ))}
    </select>
  )
}

/** "Add role…" menu — appends the picked role via onAdd (skips duplicates). */
export function RoleAdder({ roles, current, onAdd, disabled }: {
  roles: string[]
  current: string[]
  onAdd: (role: string) => void
  disabled?: boolean
}) {
  const available = roles.filter((r) => !current.includes(r))
  if (available.length === 0) return null
  return (
    <select
      style={pickerStyle}
      value=""
      disabled={disabled}
      title="Add a functional role this policy applies to"
      onChange={(e) => { if (e.target.value) onAdd(e.target.value) }}
    >
      <option value="">+ role…</option>
      {available.map((r) => <option key={r} value={r}>{r}</option>)}
    </select>
  )
}
